import { Injectable } from '@nestjs/common';
import { getConnection, getManager, QueryRunner, Repository } from 'typeorm';
import { InjectRepository } from '@nestjs/typeorm';
import { QcClearanceInfo } from './qc-clearance_info.entity';
import { CommonResponse } from '@gtpl/shared-models/masters';
import { QcClearanceDTO } from '@gtpl/shared-models/planning';
import { CodeListStatus, ErrorResponse, PassOrFail, ReferenceFeatures } from '@gtpl/shared-models/common-models';
import appSettings from 'apps/services/config';
import { GenericTransactionManager } from 'apps/services/backend/typeorm-transactions/generic-transaction-manager';
import { FileUploadEntity } from './file-upload.entity';
import { join } from 'path';
import * as fs from 'fs';

@Injectable()
export class QcClearanceInfoService {
  constructor(
    @InjectRepository(QcClearanceInfo)
    private readonly qcClearanceRepo: Repository<QcClearanceInfo>,
    private readonly dataSource: GenericTransactionManager
  ) {}

  async createClearance(info: QcClearanceDTO, filesData: any[]): Promise<CommonResponse> {
    const connection = getConnection();
    const queryRunner: QueryRunner = connection.createQueryRunner();
    await queryRunner.startTransaction();
  
    try {
      let clearanceRecord = await queryRunner.manager.findOne(QcClearanceInfo, {
        where: { codeListMainId: info.codeListMainId }
      });
  
      const chemicalResult = info.chemicalResult ?? clearanceRecord?.chemicalResult;
      const microResult = info.microResult ?? clearanceRecord?.microResult;
      const fgResult = info.fgResult ?? clearanceRecord?.fgResult;
      const others = info.others ?? clearanceRecord?.others;

      const isTestsComplete = chemicalResult != null && microResult != null && fgResult != null;
      const isAllFieldsComplete = isTestsComplete && others != null;

      let overallResult: PassOrFail | undefined;
      if (isTestsComplete) {
        overallResult = (chemicalResult === PassOrFail.PASS && 
                        microResult === PassOrFail.PASS && 
                        fgResult === PassOrFail.PASS) 
                        ? PassOrFail.PASS 
                        : PassOrFail.FAIL;
      }

      if (clearanceRecord) {
        const updateData: Partial<QcClearanceDTO> = {
          chemicalResult: chemicalResult,
          microResult: microResult,
          fgResult: fgResult,
          others: others,
          overallResult: overallResult,
          updatedAt: new Date(),
        };
  
        if (isAllFieldsComplete) {
          updateData.clearanceStatus = CodeListStatus.CLEARED;
          await queryRunner.manager.query(`
            UPDATE ${appSettings.planning_db}.code_list_main
            SET code_list_status = '${CodeListStatus.CLEARED}'
            WHERE code_list_main_id = ${info.codeListMainId}
          `);
        }
  
        await queryRunner.manager.update(
          QcClearanceInfo,
          { codeListMainId: info.codeListMainId },
          updateData
        );
  
        await this.saveFilesUsingFileHandler(filesData, queryRunner, clearanceRecord);
  
        await queryRunner.commitTransaction();
        return new CommonResponse(true, 1111, 'Clearance record updated successfully');
      } else {
        const newRecord = queryRunner.manager.create(QcClearanceInfo, {
          codeListMainId: info.codeListMainId,
          soId: info.soId,
          poNumber: info.poNumber,
          soItemId: info.soItemId,
          chemicalResult: info.chemicalResult,
          microResult: info.microResult,
          fgResult: info.fgResult,
          others: info.others,
          overallResult: overallResult,
          createdUser: info.createdUser,
          updatedUser: info.updatedUser,
          clearanceStatus: isAllFieldsComplete ? CodeListStatus.CLEARED : undefined,
        });
  
        await queryRunner.manager.save(newRecord);
  
        if (isAllFieldsComplete) {
          await queryRunner.manager.update(
            QcClearanceInfo, 
            { codeListMainId: info.codeListMainId }, 
            { 
              clearanceStatus: CodeListStatus.CLEARED,
              overallResult: overallResult 
            }
          );
        }
  
        await this.saveFilesUsingFileHandler(filesData, queryRunner, newRecord);
  
        await queryRunner.commitTransaction();
        return new CommonResponse(true, 1111, 'Clearance record created successfully');
      }
    } catch (error) {
      await queryRunner.rollbackTransaction();
      return new CommonResponse(false, 9999, 'Failed to create or update clearance', error.message);
    } finally {
      await queryRunner.release();
    }
  }
  

  async saveFilesUsingFileHandler(filesData: any[],queryRunner: QueryRunner,referenceData: QcClearanceInfo):Promise <CommonResponse> {
    try {
      const fileEntities = [];
      const deletedFilePaths = [];
  
      console.log('filesData:', filesData, 'referenceData:', referenceData);
  
      for (const fileItem of filesData) {
        const { file, reference } = fileItem;
        console.log(file, 'file');
  
        const fileObj = new FileUploadEntity();
        fileObj.featuresRefNo = referenceData.clearanceId;
        fileObj.featuresRefName = reference;
        fileObj.fileName = file.filename;
        fileObj.fileDescription = ' ';
        fileObj.originalName = file.originalname;
        fileObj.filePath = file.path;
        fileObj.size = file.size;
  
        console.log(`Saving file with Reference: ${reference}`);
  
        const findExistFile = await queryRunner.manager.getRepository(FileUploadEntity).findOne({where: { featuresRefNo: referenceData.clearanceId, featuresRefName: fileObj.featuresRefName }});
  
        console.log(findExistFile, 'findExistFile');
  
        if (findExistFile) {
          deletedFilePaths.push(findExistFile.fileName);
          fileObj.id = findExistFile.id;
        }
  
        fileEntities.push(fileObj);
      }
  
      console.log(fileEntities, 'fileEntities');
  
      await queryRunner.manager.getRepository(FileUploadEntity).save(fileEntities);
  
      for (const dele of deletedFilePaths) {
        await this.unlinkUnwantedFiles(dele);
      }
    } catch (error) {
      console.error("File handling error:", error);
      return new CommonResponse(false,56414, error.message);
    }
  }

  
  

  async unlinkUnwantedFiles(fileName: string) {
    const filePath = join(__dirname, "../../../../../../", "upload-files", "qc-clearance-files", fileName);
    if (fs.existsSync(filePath)) {
      fs.unlinkSync(filePath);
    }
  }


  async getFileDetailsById(req: QcClearanceDTO):Promise<CommonResponse>{
    try{
      const clearanceData = await this.qcClearanceRepo.findOne({where:{codeListMainId: req.codeListMainId}})
      const manager = getManager();
      let query = `
      SELECT qci.clearance_id AS clearanceId, cfu.features_ref_name AS featureRefName, cfu.file_name AS fileName
      FROM ${appSettings.planning_db}._common_file_upload cfu
      LEFT JOIN ${appSettings.planning_db}.qc_clearance_info qci ON qci.clearance_id = cfu.features_ref_no
      WHERE qci.clearance_id = ${clearanceData.clearanceId} AND cfu.features_ref_name = '${req.testType.toUpperCase()}'`

      const data = await manager.query(query)

      if(data.length > 0){
        return new CommonResponse(true, 1, 'Data retrieved',data)
      }else{
        return new CommonResponse(false, 0, 'No data')
      }
      return
    }catch(err){
      throw(err)
    }
  }
}
